import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Email, EmailPersonalFolderTreeNode, EmailTag, useEmailThread, useEmailTemplateLibrary, markEmailMentionsRead, revalidateMentionCaches, reimportEmailFromEml, type ReimportEmlResult } from '../useData';
import { useSWRConfig } from 'swr';
import api from '../../../services/api';
import { formatDateTime } from '../../../config/appConfig';
import { EmailDateDisplay } from './EmailDateDisplay';
import { useDisplayTimeZone } from '../../../hooks/useDisplayTimeZone';
import { API_BASE_URL } from '../../../config/apiConfig';
import ClientAutocomplete from './ClientAutocomplete';
import TodoAutocomplete from './TodoAutocomplete';
import AppointmentAutocomplete from './AppointmentAutocomplete';
import EmailBodyView from './EmailBodyView';
import {
  EMAIL_MESSAGE_CANVAS_CLASS,
  EMAIL_MESSAGE_PLAIN_TEXT_CLASS,
} from '../utils/emailMessageCanvas';
import { MailboxLocationBadge } from './MailboxLocationBadge';
import EmailPrintForm from './EmailPrintForm';
import { getNonEmbeddedAttachments } from '../utils/attachments';
import {
  isSaveFolderAvailable,
  pickSaveFolder,
  sanitizeAttachmentFilename,
  writeBlobToDirectory,
} from '../utils/saveAttachmentsToFolder';
import SaveAttachmentsToLibraryModal, { isLibraryDocumentFilename } from './SaveAttachmentsToLibraryModal';
import AttachmentTextExtractionDot from './AttachmentTextExtractionDot';
import {
  emailPartitionAtSearch,
  emailComposeThreadPath,
  composeEditAsNewQuery,
} from '../utils/emailPartitionAtQuery';
import {
  navigateToCompose,
  openComposeInNewTab,
  type ComposeReturnTarget,
} from '../utils/composeNavigation';
import { useEmailUiPreferences } from '../hooks/useEmailUiPreferences';
import { cn } from '../../../lib/utils';
import { Z_INDEX, zStyle } from '../../../lib/zLayers';
import { AnchoredMenu } from '../../../components/ui/AnchoredMenu';
import { Modal, ModalHeader, ModalTitle, ModalContent } from '../../../components/ui/Modal';
import { useTheme } from '../../../contexts/ThemeContext';
import { useSettings } from '../../../contexts/SettingsContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotification } from '../../../contexts/NotificationContext';
import EmailWorkflowControls from './EmailWorkflowControls';
import EmailThreadPanel from './EmailThreadPanel';
import EmailCommentsDrawer from './EmailCommentsDrawer';
import EmailDetailToolbar from './EmailDetailToolbar';
import { AiDraftPreview, useEmailAiDraft } from './EmailAiCrmActions';
import AiDraftProgressBanner from './AiDraftProgressBanner';
import EmailPresenceIndicator from './EmailPresenceIndicator';
import IcsSuggestionBanner from './IcsSuggestionBanner';
import { useEmailPresence } from '../hooks/useEmailPresence';
import EntityCustomFieldsSection from '../../custom-fields/EntityCustomFieldsSection';
import { CUSTOM_FIELD_ENTITY } from '../../custom-fields/entityTypes';

/** Above this count, attachment list starts collapsed so the message body stays visible */
const ATTACHMENTS_COLLAPSE_THRESHOLD = 6;

const BODY_ATTACHMENTS_SPLIT_MIN = 25;
const BODY_ATTACHMENTS_SPLIT_MAX = 85;

interface EmailDetailViewProps {
  email: Email | null;
  /** Called when email data changes (e.g. marked read). Pass updated email so list can update optimistically. */
  onEmailUpdate?: (updatedEmail?: Email) => void;
  /** Personal folders tree for "Copy to" */
  personalFolders?: EmailPersonalFolderTreeNode[];
  /** Copy current email into a personal folder */
  onCopyToFolder?: (email: Email, folderId: number) => void;
  /** User's email tags (for add-tag dropdown) */
  emailTags?: EmailTag[];
  /** When set with onOpenAiAssist, show AI button in the toolbar (application setting enableAiEmails). */
  showAiAssist?: boolean;
  onOpenAiAssist?: () => void;
  /** Narrow layout: show back control to return to list */
  onBack?: () => void;
  /** When true, show conversation thread panel and allow switching messages in the thread */
  threadedView?: boolean;
  /** List filters + pagination query string for scoped thread panel */
  listScopeQuery?: string | null;
  /** Switch to another message in the same conversation thread */
  onSelectThreadEmail?: (message: { id: number; partitionAt?: string }) => void;
  /** Where to return when closing compose opened from this detail view. */
  composeReturnTo?: ComposeReturnTarget;
  /** Open Collaboration drawer once when email loads (e.g. from mention notification). */
  openCollaborationOnMount?: boolean;
  onCollaborationOpened?: () => void;
}

const EmailDetailView: React.FC<EmailDetailViewProps> = ({
  email,
  onEmailUpdate,
  personalFolders = [],
  onCopyToFolder,
  emailTags = [],
  showAiAssist = false,
  onOpenAiAssist,
  onBack,
  threadedView = false,
  listScopeQuery,
  onSelectThreadEmail,
  composeReturnTo,
  openCollaborationOnMount = false,
  onCollaborationOpened,
}) => {
  const { colors } = useTheme();
  const { mutate: globalMutate } = useSWRConfig();
  const { showError, showSuccess } = useNotification();
  const { can, user } = useAuth();
  const { applicationSettings } = useSettings();
  const { company: templateCompanyLibrary, personal: templatePersonalLibrary, mutate: mutateTemplateLibrary } =
    useEmailTemplateLibrary();
  const canSaveCompanyToLibrary = can('company_email_templates', 'edit');
  const compactEmailView = applicationSettings?.compactEmailView === true;
  const displayTimeZone = useDisplayTimeZone();
  const dateFormatOpts = displayTimeZone ? { timeZone: displayTimeZone } : undefined;
  const { prefs: emailUiPrefs, updatePrefs: updateEmailUiPrefs } = useEmailUiPreferences();
  const navigate = useNavigate();
  const [isMarkingRead, setIsMarkingRead] = useState(false);
  const [showClientAutocomplete, setShowClientAutocomplete] = useState(false);
  const [showTodoAutocomplete, setShowTodoAutocomplete] = useState(false);
  const [showAppointmentAutocomplete, setShowAppointmentAutocomplete] = useState(false);
  const [toolbarMenuOpen, setToolbarMenuOpen] = useState(false);
  const [localEmail, setLocalEmail] = useState<Email | null>(email);
  const [commentsDrawerOpen, setCommentsDrawerOpen] = useState(false);
  const aiDraft = useEmailAiDraft({
    emailId: localEmail?.id ?? 0,
    partitionAt: localEmail?.partitionAt,
    onSaved: () => onEmailUpdate?.(),
  });
  const presenceUsers = useEmailPresence(localEmail?.id, 'viewing');
  const [readByPopoverOpen, setReadByPopoverOpen] = useState(false);
  const [loadingAttachmentIndex, setLoadingAttachmentIndex] = useState<number | null>(null);
  const [savingAllAttachments, setSavingAllAttachments] = useState(false);
  const [saveToLibraryModalOpen, setSaveToLibraryModalOpen] = useState(false);
  const [messageHeadersModalOpen, setMessageHeadersModalOpen] = useState(false);
  const [isReimportingEml, setIsReimportingEml] = useState(false);
  const [reimportReport, setReimportReport] = useState<ReimportEmlResult | null>(null);
  const reimportEmlInputRef = useRef<HTMLInputElement>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isMarkingSpam, setIsMarkingSpam] = useState(false);
  const [isMarkingNotSpam, setIsMarkingNotSpam] = useState(false);
  const [actionAnnouncement, setActionAnnouncement] = useState('');
  const [printFormOpen, setPrintFormOpen] = useState(false);
  const [replyDropdownOpen, setReplyDropdownOpen] = useState(false);
  const readByBtnRef = useRef<HTMLButtonElement>(null);
  const subjectBtnRef = useRef<HTMLButtonElement>(null);
  const [showAllTo, setShowAllTo] = useState(false);
  const [showAllCc, setShowAllCc] = useState(false);
  const [recipientMenu, setRecipientMenu] = useState<{ kind: 'from' | 'to' | 'cc' | 'subject'; key: string } | null>(null);
  const [headerCollapsed, setHeaderCollapsedState] = useState(emailUiPrefs.detailHeaderCollapsed);
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(false);
  const [bodySplitPercent, setBodySplitPercent] = useState(emailUiPrefs.bodySplitPercent);
  const [isResizingBodyAttachments, setIsResizingBodyAttachments] = useState(false);
  const bodyAttachmentsRef = useRef<HTMLDivElement>(null);
  const bodySplitPercentRef = useRef(bodySplitPercent);
  bodySplitPercentRef.current = bodySplitPercent;

  useEffect(() => {
    setHeaderCollapsedState(emailUiPrefs.detailHeaderCollapsed);
    setBodySplitPercent(emailUiPrefs.bodySplitPercent);
  }, [emailUiPrefs.detailHeaderCollapsed, emailUiPrefs.bodySplitPercent]);

  const setHeaderCollapsed = useCallback(
    (collapsed: boolean) => {
      setHeaderCollapsedState(collapsed);
      updateEmailUiPrefs({ detailHeaderCollapsed: collapsed });
    },
    [updateEmailUiPrefs],
  );

  useEffect(() => {
    setLocalEmail(email);
    setShowAllTo(false);
    setShowAllCc(false);
    setRecipientMenu(null);
    setReadByPopoverOpen(false);
    setMessageHeadersModalOpen(false);
    setCommentsDrawerOpen(false);
    setToolbarMenuOpen(false);
    const attachmentCount = getNonEmbeddedAttachments(email?.attachments).length;
    setAttachmentsExpanded(attachmentCount <= ATTACHMENTS_COLLAPSE_THRESHOLD);
  }, [email]);

  useEffect(() => {
    if (!localEmail || !openCollaborationOnMount) return;
    setCommentsDrawerOpen(true);
    onCollaborationOpened?.();
  }, [localEmail?.id, openCollaborationOnMount, onCollaborationOpened]);

  useEffect(() => {
    if (!commentsDrawerOpen || !localEmail?.id) return;
    void markEmailMentionsRead(localEmail.id).then(() => {
      void revalidateMentionCaches(globalMutate);
    });
  }, [commentsDrawerOpen, localEmail?.id, globalMutate]);

  const handleAttachmentsSplitMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingBodyAttachments(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizingBodyAttachments || !bodyAttachmentsRef.current) return;

      const rect = bodyAttachmentsRef.current.getBoundingClientRect();
      const newPercent = ((e.clientY - rect.top) / rect.height) * 100;
      const constrained = Math.max(
        BODY_ATTACHMENTS_SPLIT_MIN,
        Math.min(BODY_ATTACHMENTS_SPLIT_MAX, newPercent),
      );
      setBodySplitPercent(constrained);
    };

    const handleMouseUp = () => {
      if (isResizingBodyAttachments) {
        updateEmailUiPrefs({ bodySplitPercent: bodySplitPercentRef.current });
        setIsResizingBodyAttachments(false);
      }
    };

    if (isResizingBodyAttachments) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingBodyAttachments, updateEmailUiPrefs]);

  const partitionQuery = useMemo(
    () => emailPartitionAtSearch(localEmail?.partitionAt),
    [localEmail?.partitionAt]
  );

  const truncate20 = (s: string) => (s.length > 20 ? s.slice(0, 20) + '…' : s);

  const handleViewAttachment = async (emailId: number, index: number) => {
    setLoadingAttachmentIndex(index);
    try {
      const { data } = await api.get(`/emails/${emailId}/attachments/${index}${partitionQuery}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(data as Blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error('Failed to open attachment', err);
    } finally {
      setLoadingAttachmentIndex(null);
    }
  };

  const downloadAttachmentBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const nonEmbeddedAttachments = useMemo(
    () => getNonEmbeddedAttachments(localEmail?.attachments),
    [localEmail?.attachments],
  );

  const librarySaveableCount = useMemo(
    () => nonEmbeddedAttachments.filter((att) => isLibraryDocumentFilename(att.filename || '')).length,
    [nonEmbeddedAttachments],
  );

  const handleSaveAllAttachments = async () => {
    if (!localEmail?.id || nonEmbeddedAttachments.length === 0) return;
    setSavingAllAttachments(true);
    try {
      const directoryHandle = isSaveFolderAvailable() ? await pickSaveFolder() : null;
      if (isSaveFolderAvailable() && directoryHandle === null) {
        return;
      }

      let savedCount = 0;
      for (let i = 0; i < nonEmbeddedAttachments.length; i++) {
        const att = nonEmbeddedAttachments[i];
        const { data } = await api.get(`/emails/${localEmail.id}/attachments/${i}${partitionQuery}`, {
          responseType: 'blob',
        });
        const safeName = sanitizeAttachmentFilename(att.filename || `attachment-${i + 1}`);
        if (directoryHandle) {
          await writeBlobToDirectory(directoryHandle, safeName, data as Blob);
        } else {
          downloadAttachmentBlob(data as Blob, safeName);
          if (i < nonEmbeddedAttachments.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 200));
          }
        }
        savedCount++;
      }

      if (directoryHandle) {
        showSuccess(
          `Saved ${savedCount} attachment${savedCount !== 1 ? 's' : ''} to "${directoryHandle.name}"`,
        );
      }
    } catch (err) {
      console.error('Failed to save attachments', err);
      showError('Failed to save attachments. Please try again.');
    } finally {
      setSavingAllAttachments(false);
    }
  };

  const threadListScoped = threadedView && listScopeQuery != null && listScopeQuery.length > 0;
  const { thread: emailThread } = useEmailThread(
    localEmail?.id ?? 0,
    localEmail?.partitionAt,
    threadListScoped ? listScopeQuery : null,
  );

  const threadMessages = useMemo(() => {
    const source = emailThread?.emails ?? [];
    return [...source].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [emailThread?.emails]);

  const closeToolbarMenu = useCallback(() => setToolbarMenuOpen(false), []);

  useEffect(() => {
    if (!threadedView || !onSelectThreadEmail || !localEmail || threadMessages.length <= 1) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('input, textarea, select') || target.isContentEditable) return;
      const idx = threadMessages.findIndex((m) => m.id === localEmail.id);
      if (idx < 0) return;
      if (e.key === '[' && idx > 0) {
        e.preventDefault();
        const msg = threadMessages[idx - 1];
        onSelectThreadEmail({ id: msg.id, partitionAt: msg.partitionAt });
      } else if (e.key === ']' && idx < threadMessages.length - 1) {
        e.preventDefault();
        const msg = threadMessages[idx + 1];
        onSelectThreadEmail({ id: msg.id, partitionAt: msg.partitionAt });
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [threadedView, onSelectThreadEmail, localEmail, threadMessages]);

  const handleMarkAsRead = async () => {
    if (!localEmail || localEmail.isRead || !localEmail.id) return;
    setIsMarkingRead(true);
    try {
      await api.post(`/emails/${localEmail.id}/mark-read${partitionQuery}`);
      const updated = { ...localEmail, isRead: true };
      setLocalEmail(updated);
      onEmailUpdate?.(updated);
      setActionAnnouncement('Marked as read');
      setTimeout(() => setActionAnnouncement(''), 1000);
    } catch (error) {
      console.error('Error marking as read:', error);
    } finally {
      setIsMarkingRead(false);
    }
  };

  const handleMarkAsUnread = async () => {
    if (!localEmail || !localEmail.isRead || !localEmail.id) return;
    try {
      await api.post(`/emails/${localEmail.id}/mark-unread${partitionQuery}`);
      const updated = { ...localEmail, isRead: false };
      setLocalEmail(updated);
      onEmailUpdate?.(updated);
      setActionAnnouncement('Marked as unread');
      setTimeout(() => setActionAnnouncement(''), 1000);
    } catch (error) {
      console.error('Error marking as unread:', error);
    }
  };

  // Mark as read when viewing
  useEffect(() => {
    if (localEmail && !localEmail.isRead && localEmail.id) {
      handleMarkAsRead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localEmail?.id, localEmail?.partitionAt]);

  const handleToggleStar = async () => {
    if (!localEmail || !localEmail.id) return;
    try {
      await api.post(`/emails/${localEmail.id}/star${partitionQuery}`);
      const updated = { ...localEmail, isStarred: !localEmail.isStarred };
      setLocalEmail(updated);
      onEmailUpdate?.(updated);
    } catch (error) {
      console.error('Error toggling star:', error);
    }
  };

  const handleReply = () => {
    setReplyDropdownOpen(false);
    if (localEmail?.id) {
      openComposeInNewTab(emailComposeThreadPath(localEmail.id, 'reply', localEmail.partitionAt));
    }
  };

  const handleReplyAll = () => {
    setReplyDropdownOpen(false);
    if (localEmail?.id) {
      openComposeInNewTab(emailComposeThreadPath(localEmail.id, 'reply-all', localEmail.partitionAt));
    }
  };

  const handleForward = () => {
    if (localEmail?.id) {
      openComposeInNewTab(emailComposeThreadPath(localEmail.id, 'forward', localEmail.partitionAt));
    }
  };

  const formatAddrForEml = (a: { name?: string; email: string }) => {
    const name = (a?.name ?? '').trim();
    const email = a?.email ?? '';
    if (name && email) return `"${name.replace(/"/g, '\\"')}" <${email}>`;
    return email || '';
  };

  const handleSaveAsEml = async () => {
    if (!localEmail?.id) return;
    closeToolbarMenu();
    let email = localEmail;
    // If body is missing (e.g. from list payload), fetch full email
    const hasBody = !!(email.bodyHtml ?? email.bodyText);
    if (!hasBody) {
      try {
        const { data } = await api.get<Email>(`/emails/${email.id}${emailPartitionAtSearch(email.partitionAt)}`);
        email = data;
      } catch (err) {
        console.error('Failed to load full email for EML', err);
        return;
      }
    }
    const fromList = Array.isArray(email.from) ? email.from : email.from ? [email.from] : [];
    const toList = Array.isArray(email.to) ? email.to : email.to ? [email.to] : [];
    const ccList = Array.isArray(email.cc) ? email.cc : email.cc ? [email.cc] : [];
    const fromStr = fromList.map(formatAddrForEml).filter(Boolean).join(', ');
    const toStr = toList.map(formatAddrForEml).filter(Boolean).join(', ');
    const ccStr = ccList.map(formatAddrForEml).filter(Boolean).join(', ');
    const date = email.date ? new Date(email.date).toUTCString() : new Date().toUTCString();
    const subject = (email.subject ?? '').replace(/\r?\n/g, ' ');
    const bodyContent = email.bodyHtml ?? (email.bodyText ? `<pre>${String(email.bodyText).replace(/</g, '&lt;')}</pre>` : '');
    const attachmentList = getNonEmbeddedAttachments(email.attachments);

    const buildEml = async (): Promise<Blob> => {
      if (attachmentList.length === 0) {
        const lines: string[] = [
          `From: ${fromStr}`,
          `To: ${toStr}`,
          ...(ccStr ? [`Cc: ${ccStr}`] : []),
          `Subject: ${subject}`,
          `Date: ${date}`,
          'MIME-Version: 1.0',
          'Content-Type: text/html; charset=UTF-8',
          '',
          bodyContent,
        ];
        return new Blob([lines.join('\r\n')], { type: 'message/rfc822' });
      }
      const boundary = `----=_Part_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const parts: string[] = [
        `From: ${fromStr}`,
        `To: ${toStr}`,
        ...(ccStr ? [`Cc: ${ccStr}`] : []),
        `Subject: ${subject}`,
        `Date: ${date}`,
        'MIME-Version: 1.0',
        `Content-Type: multipart/mixed; boundary="${boundary}"`,
        '',
        `--${boundary}`,
        'Content-Type: text/html; charset=UTF-8',
        'Content-Transfer-Encoding: 8bit',
        '',
        bodyContent,
      ];
      for (let i = 0; i < attachmentList.length; i++) {
        const att = attachmentList[i];
        const { data } = await api.get(`/emails/${email.id}/attachments/${i}${emailPartitionAtSearch(email.partitionAt)}`, {
          responseType: 'blob',
        });
        const base64Raw = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.indexOf('base64,') >= 0 ? result.split('base64,')[1] : result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(data as Blob);
        });
        const base64 = (base64Raw as string).replace(/\r?\n/g, '');
        const base64Lines = base64.match(/.{1,76}/g) ?? [base64];
        const safeName = (att.filename || 'attachment').replace(/[\r\n"]/g, '_');
        parts.push(`--${boundary}`);
        parts.push(`Content-Type: ${att.contentType || 'application/octet-stream'}`);
        parts.push('Content-Transfer-Encoding: base64');
        parts.push(`Content-Disposition: attachment; filename="${safeName}"`);
        parts.push('');
        parts.push(base64Lines.join('\r\n'));
      }
      parts.push(`--${boundary}--`);
      return new Blob([parts.join('\r\n')], { type: 'message/rfc822' });
    };

    try {
      const blob = await buildEml();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(subject || 'email').slice(0, 50).replace(/[^\w\s-]/g, '')}.eml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to build EML', err);
    }
  };

  const handleEditAsNew = () => {
    closeToolbarMenu();
    if (localEmail && composeReturnTo) {
      navigateToCompose(
        navigate,
        `/emails/compose${composeEditAsNewQuery(localEmail.id, localEmail.partitionAt)}`,
        { editAsNew: localEmail },
        composeReturnTo,
      );
    } else if (localEmail) {
      navigate(`/emails/compose${composeEditAsNewQuery(localEmail.id, localEmail.partitionAt)}`, {
        state: { editAsNew: localEmail },
      });
    }
  };

  const handlePrint = () => {
    closeToolbarMenu();
    setPrintFormOpen(true);
  };

  const handleReimportEmlClick = () => {
    if (!localEmail?.id || isReimportingEml) return;
    closeToolbarMenu();
    reimportEmlInputRef.current?.click();
  };

  const handleReimportEmlFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !localEmail?.id) return;
    const confirmed = window.confirm(
      'Reimport this email from the selected .eml file?\n\nBody and attachments will be replaced. Assignee, tags, read state, and links are kept.\nMessage-ID in the file must match this email.',
    );
    if (!confirmed) return;
    setIsReimportingEml(true);
    try {
      const result = await reimportEmailFromEml(file, {
        emailId: localEmail.id,
        partitionAt: localEmail.partitionAt,
      });
      const { data: refreshed } = await api.get<Email>(
        `/emails/${localEmail.id}${emailPartitionAtSearch(localEmail.partitionAt)}`,
      );
      setLocalEmail(refreshed);
      onEmailUpdate?.(refreshed);
      setReimportReport(result);
      showSuccess('Email found and reimported — see report for what changed');
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string | string[] } } }).response?.data?.message
          : undefined;
      const text = Array.isArray(message) ? message.join(', ') : message;
      showError(typeof text === 'string' && text ? text : 'Failed to reimport .eml');
    } finally {
      setIsReimportingEml(false);
    }
  };

  const handleArchive = async () => {
    if (!localEmail?.id) return;
    setIsArchiving(true);
    try {
      const endpoint = localEmail.isArchived ? 'unarchive' : 'archive';
      await api.post(`/emails/${localEmail.id}/${endpoint}${partitionQuery}`);
      const updated = { ...localEmail, isArchived: !localEmail.isArchived };
      setLocalEmail(updated);
      onEmailUpdate?.(updated);
      setActionAnnouncement(updated.isArchived ? 'Archived' : 'Unarchived');
      setTimeout(() => setActionAnnouncement(''), 1000);
    } catch (error) {
      console.error('Error toggling archive', error);
    } finally {
      setIsArchiving(false);
    }
  };

  const handleMarkSpam = async (blockSender: boolean) => {
    if (!localEmail?.id) return;
    closeToolbarMenu();
    setIsMarkingSpam(true);
    try {
      await api.post(`/emails/${localEmail.id}/mark-spam${partitionQuery}`, { blockSender });
      const updated = { ...localEmail, folder: 'JUNK' };
      setLocalEmail(updated);
      onEmailUpdate?.(updated);
      setActionAnnouncement(blockSender ? 'Marked as spam, sender blocked' : 'Marked as spam');
      setTimeout(() => setActionAnnouncement(''), 1000);
    } catch (error) {
      console.error('Error marking as spam', error);
    } finally {
      setIsMarkingSpam(false);
    }
  };

  const handleMarkNotSpam = async (unblockSender: boolean) => {
    if (!localEmail?.id) return;
    closeToolbarMenu();
    setIsMarkingNotSpam(true);
    try {
      await api.post(`/emails/${localEmail.id}/mark-not-spam${partitionQuery}`, { unblockSender });
      const updated = { ...localEmail, folder: 'INBOX' };
      setLocalEmail(updated);
      onEmailUpdate?.(updated);
      setActionAnnouncement(unblockSender ? 'Moved to Inbox, sender unblocked' : 'Moved to Inbox');
      setTimeout(() => setActionAnnouncement(''), 1000);
    } catch (error) {
      console.error('Error marking as not spam', error);
    } finally {
      setIsMarkingNotSpam(false);
    }
  };

  const handleAddTag = async (tagId: number) => {
    if (!localEmail?.id) return;
    closeToolbarMenu();
    try {
      const { data: updated } = await api.post<Email>(`/emails/${localEmail.id}/tags/${tagId}${partitionQuery}`);
      setLocalEmail(updated);
      onEmailUpdate?.(updated);
    } catch (error) {
      console.error('Error adding tag', error);
    }
  };

  const handleRemoveTag = async (tagId: number) => {
    if (!localEmail?.id) return;
    try {
      await api.delete(`/emails/${localEmail.id}/tags/${tagId}${partitionQuery}`);
      const updated = {
        ...localEmail,
        tagIds: localEmail.tagIds?.filter((id) => id !== tagId) ?? [],
        tags: localEmail.tags?.filter((t) => t.id !== tagId) ?? [],
      };
      setLocalEmail(updated);
      onEmailUpdate?.(updated);
    } catch (error) {
      console.error('Error removing tag', error);
    }
  };

  if (!localEmail) {
    return (
      <div className={cn('h-full flex items-center justify-center', colors.background)}>
        <div className={cn('text-center', colors.textMuted)}>
          <p className="text-lg mb-2">Select an email to view</p>
          <p className="text-sm">Click on an email from the list to see its content here.</p>
        </div>
      </div>
    );
  }

  // Normalize from/to/cc: API may use .email or .address; handle array/single object/string
  const addrEmail = (a: any) => (a && (a.email ?? a.address ?? (typeof a === 'string' ? a : ''))) || '';
  const addrName = (a: any) => {
    if (!a) return '';
    if (typeof a === 'string') return a;
    return a.name || addrEmail(a) || '';
  };
  const fromRaw = Array.isArray(localEmail.from) ? localEmail.from[0] : localEmail.from;
  const fromEmail = addrEmail(fromRaw);
  const fromName = addrName(fromRaw) || (fromEmail && fromEmail.split('@')[0]) || 'Unknown';
  const senderInitial = fromName[0]?.toUpperCase() || 'E';

  const toList = Array.isArray(localEmail.to) ? localEmail.to : localEmail.to ? [localEmail.to] : [];
  const ccList = Array.isArray(localEmail.cc) ? localEmail.cc : localEmail.cc ? [localEmail.cc] : [];
  const bccList = Array.isArray(localEmail.bcc) ? localEmail.bcc : localEmail.bcc ? [localEmail.bcc] : [];
  const formatAddr = (a: any) => {
    const email = addrEmail(a);
    const name = addrName(a);
    if (name && email) return `${name} <${email}>`;
    return name || email || '—';
  };
  const toDisplay = toList.map(formatAddr).join(', ');
  const ccDisplay = ccList.map(formatAddr).join(', ');
  const bccDisplay = bccList.map(formatAddr).join(', ');
  const fromDisplay = fromRaw ? formatAddr(fromRaw) : '—';

  const allRecipientEmails = (() => {
    const seen = new Set<string>();
    const emails: string[] = [];
    const push = (address: unknown) => {
      const email = addrEmail(address).trim();
      if (!email) return;
      const key = email.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      emails.push(email);
    };
    if (fromRaw) push(fromRaw);
    for (const address of toList) push(address);
    for (const address of ccList) push(address);
    for (const address of bccList) push(address);
    return emails;
  })();

  const mailboxAccountLabel =
    localEmail.emailAccount?.displayName?.trim() ||
    localEmail.emailAccount?.emailAddress?.trim() ||
    `Account #${localEmail.emailAccountId}`;
  const mailboxFolderLabel = (localEmail.folder || 'INBOX').trim() || 'INBOX';
  const mailboxLocationLine = `${mailboxAccountLabel} / ${mailboxFolderLabel}`;

  const formatShortName = (a: any) => {
    const name = addrName(a);
    const email = addrEmail(a);
    if (name) return name;
    if (email) return email.split('@')[0];
    return '—';
  };

  const getAddressKey = (a: any) => {
    const email = addrEmail(a);
    const name = addrName(a);
    return (email || name || '').toLowerCase();
  };

  const normalizedPriority = (localEmail.priority || '').toLowerCase();
  const priorityLabel =
    normalizedPriority === 'high' ? 'High'
    : normalizedPriority === 'low' ? 'Low'
    : null;

  const handleRecipientAction = async (action: 'compose' | 'copyEmail' | 'copyNameEmail' | 'filterFrom' | 'filterTo', kind: 'from' | 'to' | 'cc', address: any) => {
    const emailAddr = addrEmail(address);
    const name = addrName(address);
    if (!emailAddr && action !== 'filterFrom' && action !== 'filterTo') {
      return;
    }
    switch (action) {
      case 'compose':
        if (emailAddr) {
          if (composeReturnTo) {
            navigateToCompose(navigate, '/emails/compose', { initialTo: emailAddr }, composeReturnTo);
          } else {
            navigate('/emails/compose', { state: { initialTo: emailAddr } });
          }
        }
        break;
      case 'copyEmail': {
        if (!emailAddr) break;
        const textToCopy = emailAddr;
        try {
          if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(textToCopy);
          } else {
            const textarea = document.createElement('textarea');
            textarea.value = textToCopy;
            textarea.style.position = 'fixed';
            textarea.style.left = '-9999px';
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
          }
          setActionAnnouncement('Email address copied');
          setTimeout(() => setActionAnnouncement(''), 1000);
        } catch (e) {
          console.error('Failed to copy email address', e);
        }
        break;
      }
      case 'copyNameEmail': {
        const formatted = name && emailAddr ? `${name} <${emailAddr}>` : emailAddr || name || '';
        if (!formatted) break;
        try {
          if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(formatted);
          } else {
            const textarea = document.createElement('textarea');
            textarea.value = formatted;
            textarea.style.position = 'fixed';
            textarea.style.left = '-9999px';
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
          }
          setActionAnnouncement('Name and email copied');
          setTimeout(() => setActionAnnouncement(''), 1000);
        } catch (e) {
          console.error('Failed to copy name/email', e);
        }
        break;
      }
      case 'filterFrom':
      case 'filterTo': {
        if (!emailAddr) return;
        const searchIn = action === 'filterFrom' ? 'sender' : 'recipients';
        navigate(`/emails?search=${encodeURIComponent(emailAddr)}&searchIn=${searchIn}`);
        break;
      }
      default:
        break;
    }
    setRecipientMenu(null);
  };

  const subjectText = localEmail.subject?.trim() || '';
  const subjectDisplay = subjectText || '(no subject)';
  const hasMessageHeaders = localEmail.messageHeaders != null && localEmail.messageHeaders !== '';

  const handleFilterBySubject = () => {
    if (!subjectText) return;
    navigate(`/emails?search=${encodeURIComponent(subjectText)}&searchIn=subject`);
    setRecipientMenu(null);
  };

  const handleCopyAllAddresses = async () => {
    if (allRecipientEmails.length === 0) return;
    const text = allRecipientEmails.join(', ');
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      const count = allRecipientEmails.length;
      const message = count === 1 ? 'Copied 1 address' : `Copied ${count} addresses`;
      setActionAnnouncement(message);
      showSuccess(message);
      setTimeout(() => setActionAnnouncement(''), 1000);
    } catch (e) {
      console.error('Failed to copy addresses', e);
      showError('Could not copy addresses');
    }
  };

  const renderCopyAllAddressesButton = () => {
    if (allRecipientEmails.length === 0) return null;
    const count = allRecipientEmails.length;
    return (
      <button
        type="button"
        onClick={() => void handleCopyAllAddresses()}
        className={cn(
          'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium shrink-0',
          colors.textMuted,
          colors.interactiveHover,
        )}
        title={`Copy all ${count} unique addresses (From, To, Cc, Bcc). Paste into To — addresses are separated by commas.`}
        aria-label={`Copy all ${count} addresses`}
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
        <span>Copy all</span>
      </button>
    );
  };

  const handleCopySubject = async () => {
    if (!subjectText) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(subjectText);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = subjectText;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setActionAnnouncement('Subject copied');
      setTimeout(() => setActionAnnouncement(''), 1000);
    } catch (e) {
      console.error('Failed to copy subject', e);
    }
    setRecipientMenu(null);
  };

  const subjectMenuOpen = recipientMenu?.kind === 'subject';

  const renderReadByMenu = (wrapperClassName?: string) => {
    if (!localEmail.readBy?.length) return null;
    return (
      <div className={cn('relative flex-shrink-0', wrapperClassName)}>
        <button
          ref={readByBtnRef}
          type="button"
          onClick={() => setReadByPopoverOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 text-slate-800 px-2 py-1 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 transition-colors"
          title="Click to see all readers"
          aria-expanded={readByPopoverOpen}
          aria-haspopup="menu"
        >
          <svg className="w-3.5 h-3.5 text-slate-600 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          <span>
            Read by: {localEmail.readBy.length === 1
              ? `${localEmail.readBy[0].name}${localEmail.readBy[0].readDate ? ` (${formatDateTime(localEmail.readBy[0].readDate)})` : ''}`
              : `${localEmail.readBy.length} users`}
          </span>
        </button>
        <AnchoredMenu
          open={readByPopoverOpen}
          onClose={() => setReadByPopoverOpen(false)}
          anchorRef={readByBtnRef}
          align="end"
          className={cn('min-w-[200px] py-2', colors.surface, colors.border)}
        >
          <div className={cn('px-3 py-1.5 text-xs font-medium border-b', colors.textMuted, colors.divide)}>Read by</div>
          {localEmail.readBy.map((r) => (
            <div key={r.userId} className={cn('px-3 py-2 text-xs border-b last:border-0', colors.text, colors.divide)}>
              <span className="font-medium">{r.name}</span>
              {r.email && <span className={cn(colors.textMuted)}> ({r.email})</span>}
              {r.readDate && <div className={cn('mt-0.5', colors.textMuted)}>{formatDateTime(r.readDate)}</div>}
            </div>
          ))}
        </AnchoredMenu>
      </div>
    );
  };

  const renderEmailBodyContent = () => (
    <div className="email-body-print-inner w-full px-2 sm:px-3 py-2 min-w-0">
      <div className={cn('px-3 py-2 sm:px-4 sm:py-3 min-w-0', EMAIL_MESSAGE_CANVAS_CLASS)}>
        {localEmail.bodyHtml ? (
          <EmailBodyView
            bodyHtml={localEmail.bodyHtml}
            emailId={localEmail.id}
            apiBaseUrl={API_BASE_URL}
            emailPartitionAt={localEmail.partitionAt}
          />
        ) : (
          <pre className={EMAIL_MESSAGE_PLAIN_TEXT_CLASS}>
            {localEmail.bodyText || '(no content)'}
          </pre>
        )}
      </div>
    </div>
  );

  const renderAttachmentsHeader = () => (
    <div className="flex items-center gap-2 flex-shrink-0">
      <button
        type="button"
        onClick={() => setAttachmentsExpanded((v) => !v)}
        className={cn(
          'flex items-center gap-2 flex-1 min-w-0 text-left rounded-md -mx-1 px-1 py-0.5',
          colors.interactiveHover,
        )}
        aria-expanded={attachmentsExpanded}
        title={attachmentsExpanded ? 'Hide attachments' : 'Show attachments'}
      >
        <svg className={cn('w-5 h-5 flex-shrink-0', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
        </svg>
        <span className={cn('text-sm font-medium flex-1 min-w-0', colors.text)}>
          {nonEmbeddedAttachments.length} Attachment{nonEmbeddedAttachments.length !== 1 ? 's' : ''}
        </span>
        <svg
          className={cn('w-4 h-4 flex-shrink-0 transition-transform', colors.textMuted, attachmentsExpanded && 'rotate-180')}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <button
        type="button"
        onClick={() => void handleSaveAllAttachments()}
        disabled={savingAllAttachments}
        className={cn(
          'flex-shrink-0 p-1.5 rounded-md border transition-colors disabled:opacity-60 disabled:cursor-wait',
          colors.border,
          colors.textMuted,
          colors.interactiveHover,
        )}
        title={isSaveFolderAvailable() ? 'Save all attachments to a folder' : 'Save all attachments'}
        aria-label={isSaveFolderAvailable() ? 'Save all attachments to a folder' : 'Save all attachments'}
      >
        {savingAllAttachments ? (
          <span className="w-4 h-4 block border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin" />
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        )}
      </button>
      <button
        type="button"
        onClick={() => setSaveToLibraryModalOpen(true)}
        disabled={librarySaveableCount === 0}
        className={cn(
          'flex-shrink-0 p-1.5 rounded-md border transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
          colors.border,
          colors.textMuted,
          colors.interactiveHover,
        )}
        title={
          librarySaveableCount === 0
            ? 'No PDF, Word, or Excel attachments to save to library'
            : 'Save attachments to library'
        }
        aria-label="Save attachments to library"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11v6m-3-3h6" />
        </svg>
      </button>
    </div>
  );

  const renderAttachmentItems = () => (
    <div className="mt-2 flex flex-wrap gap-2 content-start">
      {nonEmbeddedAttachments.map((att, idx) => (
        <span key={idx} className="relative inline-block max-w-full">
          <AttachmentTextExtractionDot
            index={idx}
            meta={att}
            emailAttachments={localEmail?.emailAttachments ?? null}
          />
          <button
            type="button"
            onClick={() => localEmail?.id && handleViewAttachment(localEmail.id, idx)}
            disabled={loadingAttachmentIndex === idx}
            className={cn('inline-flex items-center gap-2 max-w-full px-2.5 py-1.5 rounded border transition-colors text-sm text-left cursor-pointer disabled:opacity-60 disabled:cursor-wait', colors.surface, colors.border, colors.interactiveHover, colors.text)}
            title={`View ${att.filename || `Attachment ${idx + 1}`}`}
          >
            {loadingAttachmentIndex === idx ? (
              <span className="w-4 h-4 flex-shrink-0 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin" />
            ) : (
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            )}
            <span className={cn('truncate max-w-[200px]', colors.text)} title={att.filename || `Attachment ${idx + 1}`}>
              {att.filename || `Attachment ${idx + 1}`}
            </span>
            {att.size != null && (
              <span className={cn('text-xs flex-shrink-0', colors.textMuted)}>
                {(att.size / 1024).toFixed(2)} KB
              </span>
            )}
          </button>
        </span>
      ))}
    </div>
  );

  return (
    <div className={cn('h-full flex flex-col email-detail-root', colors.background)} role="main" aria-label="Email message">
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {actionAnnouncement}
      </div>
      {recipientMenu && recipientMenu.kind !== 'subject' && (
        <div
          className="fixed inset-0 z-20"
          aria-hidden="true"
          onClick={() => setRecipientMenu(null)}
        />
      )}
      <AnchoredMenu
        open={subjectMenuOpen}
        onClose={() => setRecipientMenu(null)}
        anchorRef={subjectBtnRef}
        className={cn('min-w-[200px] max-w-[320px] py-0 text-xs', colors.surface, colors.border)}
      >
        <div className={cn('px-3 py-1.5 text-[11px] border-b truncate', colors.textMuted, colors.border)}>
          {subjectDisplay}
        </div>
        <button
          type="button"
          className={cn(
            'w-full text-left px-3 py-2',
            colors.text,
            colors.interactiveHover,
            !subjectText && 'opacity-50 cursor-not-allowed',
          )}
          disabled={!subjectText}
          onClick={() => void handleCopySubject()}
        >
          Copy Subject
        </button>
        <button
          type="button"
          className={cn(
            'w-full text-left px-3 py-2 border-t',
            colors.text,
            colors.border,
            colors.interactiveHover,
            !subjectText && 'opacity-50 cursor-not-allowed',
          )}
          disabled={!subjectText}
          onClick={handleFilterBySubject}
        >
          Filter Messages By Subject
        </button>
      </AnchoredMenu>
      <EmailDetailToolbar
        email={localEmail}
        onBack={onBack}
        commentsOpen={commentsDrawerOpen}
        onToggleComments={() => setCommentsDrawerOpen((v) => !v)}
        toolbarMenuOpen={toolbarMenuOpen}
        onToolbarMenuOpenChange={setToolbarMenuOpen}
        replyDropdownOpen={replyDropdownOpen}
        onReplyDropdownOpenChange={setReplyDropdownOpen}
        onToggleStar={() => void handleToggleStar()}
        onReply={handleReply}
        onReplyAll={handleReplyAll}
        onForward={handleForward}
        onMarkAsRead={() => void handleMarkAsRead()}
        onMarkAsUnread={() => void handleMarkAsUnread()}
        onArchive={() => { if (!localEmail.isArchived) void handleArchive(); }}
        onUnarchive={() => { if (localEmail.isArchived) void handleArchive(); }}
        onLinkClient={() => setShowClientAutocomplete(true)}
        onLinkTodo={() => setShowTodoAutocomplete(true)}
        onLinkAppointment={() => setShowAppointmentAutocomplete(true)}
        onCopyToFolder={onCopyToFolder ? (folderId) => onCopyToFolder(localEmail, folderId) : undefined}
        personalFolders={personalFolders}
        emailTags={emailTags}
        onAddTag={(tagId) => void handleAddTag(tagId)}
        onRemoveTag={(tagId) => void handleRemoveTag(tagId)}
        showAiAssist={showAiAssist}
        onOpenAiAssist={onOpenAiAssist}
        showAiDraft={aiDraft.enabled && Boolean(localEmail?.id)}
        onDraftTodo={aiDraft.handleDraftTodo}
        onDraftAppointment={aiDraft.handleDraftAppointment}
        aiDraftLoading={aiDraft.loading}
        hasMessageHeaders={hasMessageHeaders}
        onViewHeaders={() => setMessageHeadersModalOpen(true)}
        onSaveAsEml={() => void handleSaveAsEml()}
        onReimportEml={user?.role === 'ADMIN' ? handleReimportEmlClick : undefined}
        isReimportingEml={isReimportingEml}
        onEditAsNew={handleEditAsNew}
        onPrint={handlePrint}
        onMarkSpam={(block) => void handleMarkSpam(block)}
        onMarkNotSpam={(unblock) => void handleMarkNotSpam(unblock)}
        isMarkingRead={isMarkingRead}
        isArchiving={isArchiving}
        isMarkingSpam={isMarkingSpam}
        isMarkingNotSpam={isMarkingNotSpam}
      />
      {user?.role === 'ADMIN' && (
        <input
          ref={reimportEmlInputRef}
          type="file"
          accept=".eml,message/rfc822"
          className="hidden"
          onChange={(e) => void handleReimportEmlFile(e)}
        />
      )}

      {aiDraft.loading ? (
        <div className="flex-shrink-0 px-2 sm:px-3 py-2 print:hidden">
          <AiDraftProgressBanner draftType={aiDraft.loading} />
        </div>
      ) : null}

      {aiDraft.draftPreview ? (
        <div className="flex-shrink-0 px-2 sm:px-3 py-2 print:hidden">
          <AiDraftPreview
            draft={aiDraft.draftPreview}
            onOpenForm={aiDraft.openDraftForm}
            onSaveDirectly={aiDraft.saveDraftDirectly}
            saving={aiDraft.saving}
            onDismiss={aiDraft.dismissPreview}
          />
        </div>
      ) : null}

      {threadedView && onSelectThreadEmail && listScopeQuery && (
        <EmailThreadPanel
          emailId={localEmail.id}
          partitionAt={localEmail.partitionAt}
          listScopeQuery={listScopeQuery}
          onSelectMessage={onSelectThreadEmail}
        />
      )}

      {/* Email Content: details outside, single scroll for body only */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Print-only header: From, To, Cc, Subject, Date */}
        <div className="email-print-header hidden print:block print:mb-4 print:pb-4 print:border-b print:border-gray-300">
          <dl className="text-sm text-gray-900 space-y-1">
            <div><dt className="inline font-semibold w-14">From:</dt> <dd className="inline">{fromDisplay}</dd></div>
            <div><dt className="inline font-semibold w-14">To:</dt> <dd className="inline">{toDisplay || '—'}</dd></div>
            {ccDisplay ? <div><dt className="inline font-semibold w-14">Cc:</dt> <dd className="inline">{ccDisplay}</dd></div> : null}
            <div><dt className="inline font-semibold w-14">Subject:</dt> <dd className="inline">{localEmail.subject || '(no subject)'}</dd></div>
            <div><dt className="inline font-semibold w-14">Location:</dt> <dd className="inline">{mailboxLocationLine}</dd></div>
            <div><dt className="inline font-semibold w-14">Date:</dt> <dd className="inline">{formatDateTime(localEmail.date, dateFormatOpts)}</dd></div>
          </dl>
        </div>
        {/* Message details - full width (hidden when printing); expand/collapse + compact or full layout */}
        <div className="email-detail-meta flex-shrink-0 w-full px-2 sm:px-3 pt-1 pb-0.5">
          <div className="mb-1 w-full print:hidden">
            <EmailWorkflowControls
              className="w-full"
              email={localEmail}
              onEmailUpdate={(updated) => {
                if (updated) {
                  setLocalEmail(updated);
                  onEmailUpdate?.(updated);
                }
              }}
            />
          </div>
          <EmailPresenceIndicator users={presenceUsers} className="mb-1 print:hidden" />
          {headerCollapsed ? (
            <div className={cn('rounded-t-lg border border-b-0 px-2.5 py-1 flex items-start justify-between gap-2', colors.surface, colors.border)}>
              <button
                type="button"
                onClick={() => setHeaderCollapsed(false)}
                className={cn('flex items-center gap-2 min-w-0 flex-1 text-left', colors.text, colors.interactiveHover)}
                title="Expand header"
              >
                <span className="h-5 w-5 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 text-white text-[10px] font-semibold">{senderInitial}</span>
                <span className={cn('text-xs truncate min-w-0', colors.textMuted)}>
                  {fromName} · {(() => { const s = localEmail.subject || '(no subject)'; return s.length > 50 ? s.slice(0, 50) + '…' : s; })()}
                </span>
              </button>
              <div className="flex flex-col items-end gap-2 flex-shrink-0 text-right min-w-0 max-w-[48%] sm:max-w-[42%]">
                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                  {priorityLabel && (
                    <span
                      className={cn(
                        'text-[10px] px-1.5 py-0.5 rounded-full border font-semibold whitespace-nowrap',
                        normalizedPriority === 'high'
                          ? 'border-red-500 text-red-600 bg-red-50 dark:bg-red-900/20'
                          : 'border-amber-400 text-amber-700 bg-amber-50 dark:bg-amber-900/20'
                      )}
                      title="Message priority"
                    >
                      {priorityLabel}
                    </span>
                  )}
                  <EmailDateDisplay
                    date={localEmail.date}
                    messageHeaders={localEmail.messageHeaders}
                    receivedDate={localEmail.receivedDate}
                    compact
                    className={cn('text-xs whitespace-nowrap', colors.textMuted)}
                    mutedClassName={colors.textMuted}
                  />
                </div>
                <MailboxLocationBadge
                  variant="compact"
                  accountLabel={mailboxAccountLabel}
                  folderLabel={mailboxFolderLabel}
                  className="w-full max-w-full"
                />
              </div>
              {renderCopyAllAddressesButton()}
              <button
                type="button"
                onClick={() => setHeaderCollapsed(false)}
                className={cn('flex-shrink-0 p-1.5 rounded', colors.textMuted, colors.interactiveHover)}
                title="Expand header"
                aria-label="Expand header"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          ) : compactEmailView ? (
            <div className={cn('rounded-t-lg border border-b-0 px-2.5 py-1.5 space-y-1', colors.surface, colors.border)}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <span className="h-6 w-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 text-white text-xs font-semibold">{senderInitial}</span>
                  <span className={cn('text-sm font-medium truncate', colors.text)}>{fromName}</span>
                </div>
                <div className="flex items-start gap-1 flex-shrink-0">
                  <div className="flex flex-col items-end gap-2 text-right min-w-0 max-w-[13rem]">
                    <EmailDateDisplay
                    date={localEmail.date}
                    messageHeaders={localEmail.messageHeaders}
                    receivedDate={localEmail.receivedDate}
                    compact
                    className={cn('text-xs whitespace-nowrap', colors.textMuted)}
                    mutedClassName={colors.textMuted}
                  />
                    <MailboxLocationBadge
                      variant="compact"
                      accountLabel={mailboxAccountLabel}
                      folderLabel={mailboxFolderLabel}
                      className="max-w-full"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setHeaderCollapsed(true)}
                    className={cn('flex-shrink-0 p-1 rounded', colors.textMuted, colors.interactiveHover)}
                    title="Collapse header to give more space to body"
                    aria-label="Collapse header"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-1 min-w-0">
                {renderCopyAllAddressesButton()}
                <div className={cn('text-xs truncate min-w-0', colors.textMuted)} title={[toDisplay, ccDisplay, bccDisplay].filter(Boolean).join(' · ')}>
                  To: {toDisplay || '—'}{ccDisplay ? ` · Cc: ${ccDisplay}` : ''}{bccDisplay ? ` · Bcc: ${bccDisplay}` : ''}
                </div>
              </div>
              <div className="min-w-0">
                <button
                  ref={subjectBtnRef}
                  type="button"
                  className={cn('text-left w-full min-w-0', subjectText && colors.interactiveHover)}
                  title={subjectDisplay}
                  onClick={() => subjectText && setRecipientMenu({ kind: 'subject', key: 'subject' })}
                  disabled={!subjectText}
                >
                  <h1 className={cn('text-sm font-semibold truncate', colors.text)}>{subjectDisplay}</h1>
                </button>
              </div>
            </div>
          ) : (
          <div className={cn('rounded-t-lg border border-b-0 px-2.5 sm:px-3 py-2 space-y-1.5', colors.surface, colors.border)}>
            {/* Row 1: sender + priority + date */}
            <div className="flex items-start gap-3">
              <div className="relative flex items-center gap-2 min-w-0">
                <span className="h-7 w-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 text-white text-xs font-semibold">
                  {senderInitial}
                </span>
                <div className="flex flex-col min-w-0">
                  <button
                    type="button"
                    className="text-left"
                    onClick={() => setRecipientMenu({ kind: 'from', key: getAddressKey(fromRaw) })}
                  >
                    <span className={cn('text-sm font-semibold truncate', colors.text)}>{fromName}</span>
                    {fromEmail && (
                      <span className={cn('block text-xs truncate', colors.textMuted)}>
                        &lt;{fromEmail}&gt;
                      </span>
                    )}
                  </button>
                  {recipientMenu && recipientMenu.kind === 'from' && recipientMenu.key === getAddressKey(fromRaw) && (
                    <div
                      className={cn(
                        'absolute left-0 sm:left-auto sm:right-0 top-full mt-1 min-w-[180px] max-w-[260px] rounded-lg border shadow-lg text-xs',
                        colors.surface,
                        colors.border
                      )}
                      style={zStyle(Z_INDEX.localDropdown)}
                    >
                      <div className={cn('px-3 py-1.5 text-[11px] border-b truncate', colors.textMuted, colors.border)}>
                        {addrEmail(fromRaw)}
                      </div>
                      <button
                        type="button"
                        className={cn('w-full text-left px-3 py-2', colors.text, colors.interactiveHover)}
                        onClick={() => handleRecipientAction('compose', 'from', fromRaw)}
                      >
                        Compose Message To
                      </button>
                      <button
                        type="button"
                        className={cn('w-full text-left px-3 py-2', colors.text, colors.interactiveHover)}
                        onClick={() => {
                          setRecipientMenu(null);
                          handleEditAsNew();
                        }}
                      >
                        Edit as new
                      </button>
                      <button
                        type="button"
                        className={cn('w-full text-left px-3 py-2', colors.text, colors.interactiveHover)}
                        onClick={() => handleRecipientAction('copyEmail', 'from', fromRaw)}
                      >
                        Copy Email Address
                      </button>
                      <button
                        type="button"
                        className={cn('w-full text-left px-3 py-2', colors.text, colors.interactiveHover)}
                        onClick={() => handleRecipientAction('copyNameEmail', 'from', fromRaw)}
                      >
                        Copy Name and Email Address
                      </button>
                      <button
                        type="button"
                        className={cn('w-full text-left px-3 py-2 border-t', colors.text, colors.border, colors.interactiveHover)}
                        onClick={() => handleRecipientAction('filterFrom', 'from', fromRaw)}
                      >
                        Filter Messages From
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="ml-auto flex items-start gap-1.5 flex-shrink-0 text-xs">
                <div className="flex flex-col items-end gap-2 text-right min-w-0 max-w-[min(100%,14rem)] sm:max-w-[18rem]">
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    {priorityLabel && (
                      <span
                        className={cn(
                          'px-2 py-0.5 rounded-full border font-semibold',
                          normalizedPriority === 'high'
                            ? 'border-red-500 text-red-600 bg-red-50 dark:bg-red-900/20'
                            : normalizedPriority === 'low'
                              ? 'border-amber-400 text-amber-700 bg-amber-50 dark:bg-amber-900/20'
                              : colors.border
                        )}
                        title="Message priority"
                      >
                        {priorityLabel}
                      </span>
                    )}
                    <EmailDateDisplay
                      date={localEmail.date}
                      messageHeaders={localEmail.messageHeaders}
                      receivedDate={localEmail.receivedDate}
                      mutedClassName={colors.textMuted}
                    />
                  </div>
                  <MailboxLocationBadge
                    accountLabel={mailboxAccountLabel}
                    folderLabel={mailboxFolderLabel}
                    className="max-w-full"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setHeaderCollapsed(true)}
                  className={cn('p-1 rounded shrink-0', colors.textMuted, colors.interactiveHover)}
                  title="Collapse header to give more space to body"
                  aria-label="Collapse header"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Row 2: To / Cc lines with chips */}
            <div className="mt-1 space-y-0.5 text-xs">
              <div className={cn('flex items-start gap-1 flex-wrap', colors.textMuted)}>
                <span className="font-semibold">To:</span>
                {renderCopyAllAddressesButton()}
                {toList.length === 0 && <span>—</span>}
                {(showAllTo ? toList : toList.slice(0, 3)).map((addr, idx) => {
                  const key = getAddressKey(addr);
                  return (
                    <div key={idx} className="relative inline-block">
                      <button
                        type="button"
                        className="px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-[11px] max-w-[180px] truncate"
                        title={formatAddr(addr)}
                        onClick={() => setRecipientMenu({ kind: 'to', key })}
                      >
                        {formatShortName(addr)}
                      </button>
                      {recipientMenu && recipientMenu.kind === 'to' && recipientMenu.key === key && (
                      <div
                        className={cn(
                          'absolute left-0 top-full mt-1 min-w-[180px] max-w-[260px] rounded-lg border shadow-lg text-xs',
                          colors.surface,
                          colors.border
                        )}
                        style={zStyle(Z_INDEX.localDropdown)}
                      >
                        <div className={cn('px-3 py-1.5 text-[11px] border-b truncate', colors.textMuted, colors.border)}>
                          {addrEmail(addr)}
                        </div>
                        <button
                          type="button"
                          className={cn('w-full text-left px-3 py-2', colors.text, colors.interactiveHover)}
                          onClick={() => handleRecipientAction('compose', 'to', addr)}
                        >
                          Compose Message To
                        </button>
                        <button
                          type="button"
                          className={cn('w-full text-left px-3 py-2', colors.text, colors.interactiveHover)}
                          onClick={() => {
                            setRecipientMenu(null);
                            handleEditAsNew();
                          }}
                        >
                          Edit as new
                        </button>
                        <button
                          type="button"
                          className={cn('w-full text-left px-3 py-2', colors.text, colors.interactiveHover)}
                          onClick={() => handleRecipientAction('copyEmail', 'to', addr)}
                        >
                          Copy Email Address
                        </button>
                        <button
                          type="button"
                          className={cn('w-full text-left px-3 py-2', colors.text, colors.interactiveHover)}
                          onClick={() => handleRecipientAction('copyNameEmail', 'to', addr)}
                        >
                          Copy Name and Email Address
                        </button>
                        <button
                          type="button"
                          className={cn('w-full text-left px-3 py-2 border-t', colors.text, colors.border, colors.interactiveHover)}
                          onClick={() => handleRecipientAction('filterTo', 'to', addr)}
                        >
                          Filter Messages To
                        </button>
                      </div>
                    )}
                    </div>
                  );
                })}
                {!showAllTo && toList.length > 3 && (
                  <button
                    type="button"
                    className={cn('text-[11px] underline-offset-2 hover:underline', colors.textMuted)}
                    onClick={() => setShowAllTo(true)}
                  >
                    +{toList.length - 3} more
                  </button>
                )}
              </div>
              {ccList.length > 0 && (
                <div className={cn('flex items-start gap-1 flex-wrap', colors.textMuted)}>
                  <span className="font-semibold">Cc:</span>
                  {(showAllCc ? ccList : ccList.slice(0, 3)).map((addr, idx) => {
                    const key = getAddressKey(addr);
                    return (
                      <div key={idx} className="relative inline-block">
                        <button
                          type="button"
                          className="px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-[11px] max-w-[180px] truncate"
                          title={formatAddr(addr)}
                          onClick={() => setRecipientMenu({ kind: 'cc', key })}
                        >
                          {formatShortName(addr)}
                        </button>
                        {recipientMenu && recipientMenu.kind === 'cc' && recipientMenu.key === key && (
                        <div
                          className={cn(
                            'absolute left-0 top-full mt-1 min-w-[180px] max-w-[260px] rounded-lg border shadow-lg text-xs',
                            colors.surface,
                            colors.border
                          )}
                          style={zStyle(Z_INDEX.localDropdown)}
                        >
                          <div className={cn('px-3 py-1.5 text-[11px] border-b truncate', colors.textMuted, colors.border)}>
                            {addrEmail(addr)}
                          </div>
                          <button
                            type="button"
                            className={cn('w-full text-left px-3 py-2', colors.text, colors.interactiveHover)}
                            onClick={() => handleRecipientAction('compose', 'cc', addr)}
                          >
                            Compose Message To
                          </button>
                          <button
                            type="button"
                            className={cn('w-full text-left px-3 py-2', colors.text, colors.interactiveHover)}
                            onClick={() => {
                              setRecipientMenu(null);
                              handleEditAsNew();
                            }}
                          >
                            Edit as new
                          </button>
                          <button
                            type="button"
                            className={cn('w-full text-left px-3 py-2', colors.text, colors.interactiveHover)}
                            onClick={() => handleRecipientAction('copyEmail', 'cc', addr)}
                          >
                            Copy Email Address
                          </button>
                          <button
                            type="button"
                            className={cn('w-full text-left px-3 py-2', colors.text, colors.interactiveHover)}
                            onClick={() => handleRecipientAction('copyNameEmail', 'cc', addr)}
                          >
                            Copy Name and Email Address
                          </button>
                          <button
                            type="button"
                            className={cn('w-full text-left px-3 py-2 border-t', colors.text, colors.border, colors.interactiveHover)}
                            onClick={() => handleRecipientAction('filterTo', 'cc', addr)}
                          >
                            Filter Messages To
                          </button>
                        </div>
                      )}
                      </div>
                    );
                  })}
                  {!showAllCc && ccList.length > 3 && (
                    <button
                      type="button"
                      className={cn('text-[11px] underline-offset-2 hover:underline', colors.textMuted)}
                      onClick={() => setShowAllCc(true)}
                    >
                      +{ccList.length - 3} more
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Row 3: Subject */}
            <div className="min-w-0 mt-1.5">
              <button
                ref={subjectBtnRef}
                type="button"
                className={cn('text-left w-full min-w-0 pr-2', subjectText && colors.interactiveHover)}
                title={subjectDisplay}
                onClick={() => subjectText && setRecipientMenu({ kind: 'subject', key: 'subject' })}
                disabled={!subjectText}
              >
                <h1 className={cn('text-base sm:text-lg font-semibold truncate', colors.text)}>
                  {subjectDisplay}
                </h1>
              </button>
            </div>

          </div>
          )}

          {!headerCollapsed && (
          <>
          {/* Links + Read by row - ticket/client/task/todo/appointment pills left, read-by pill right (hidden when printing) */}
          {(localEmail.linkedClient || localEmail.linkedTodo || localEmail.linkedAppointment || (localEmail.readBy && localEmail.readBy.length > 0)) && (
            <div className={cn('email-detail-links border-x px-2 sm:px-3 py-1 bg-blue-50 dark:bg-blue-900/20 rounded-b-lg', colors.border)}>
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                <div className="flex flex-wrap items-center gap-2">
                {localEmail.linkedClient && (
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-indigo-100 text-indigo-800 px-2 py-1 max-w-full min-w-0">
                    <svg className="w-3.5 h-3.5 flex-shrink-0 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <Link
                      to={`/clients/${localEmail.linkedClient.clientId}`}
                      className="truncate text-indigo-800 hover:text-indigo-900 font-medium"
                      target="_blank"
                      title={localEmail.linkedClient.clientName || `Client #${localEmail.linkedClient.clientId}`}
                    >
                      {truncate20(localEmail.linkedClient.clientName || `Client #${localEmail.linkedClient.clientId}`)}
                    </Link>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await api.post(`/emails/${localEmail.id}/unlink-client${partitionQuery}`);
                          setLocalEmail({ ...localEmail, linkedClient: undefined });
                          onEmailUpdate?.();
                        } catch (error) {
                          console.error('Error unlinking client:', error);
                        }
                      }}
                      className="flex-shrink-0 text-red-600 hover:text-red-800 p-0.5 rounded"
                      title="Unlink client"
                      aria-label="Unlink client"
                    >
                      ✕
                    </button>
                  </span>
                )}
                {localEmail.linkedTodo && (
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-teal-100 text-teal-800 px-2 py-1 max-w-full min-w-0">
                    <svg className="w-3.5 h-3.5 flex-shrink-0 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                    <Link
                      to={`/todos/${localEmail.linkedTodo.todoId}`}
                      className="truncate text-teal-800 hover:text-teal-900 font-medium"
                      target="_blank"
                      title={localEmail.linkedTodo.todoDescr || `Todo #${localEmail.linkedTodo.todoId}`}
                    >
                      {truncate20(localEmail.linkedTodo.todoDescr || `Todo #${localEmail.linkedTodo.todoId}`)}
                    </Link>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await api.post(`/emails/${localEmail.id}/unlink-todo${partitionQuery}`);
                          setLocalEmail({ ...localEmail, linkedTodo: undefined });
                          onEmailUpdate?.();
                        } catch (err) {
                          console.error('Error unlinking todo:', err);
                        }
                      }}
                      className="flex-shrink-0 text-red-600 hover:text-red-800 p-0.5 rounded"
                      title="Unlink todo"
                      aria-label="Unlink todo"
                    >
                      ✕
                    </button>
                  </span>
                )}
                {localEmail.linkedAppointment && (
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-sky-100 text-sky-800 px-2 py-1 max-w-full min-w-0">
                    <svg className="w-3.5 h-3.5 flex-shrink-0 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <Link
                      to={`/appointments/${localEmail.linkedAppointment.clientAppId}`}
                      className="truncate text-sky-800 hover:text-sky-900 font-medium"
                      target="_blank"
                      title={localEmail.linkedAppointment.clientAppName || `Appointment #${localEmail.linkedAppointment.clientAppId}`}
                    >
                      {truncate20(localEmail.linkedAppointment.clientAppName || `Appointment #${localEmail.linkedAppointment.clientAppId}`)}
                    </Link>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await api.post(`/emails/${localEmail.id}/unlink-appointment${partitionQuery}`);
                          setLocalEmail({ ...localEmail, linkedAppointment: undefined });
                          onEmailUpdate?.();
                        } catch (err) {
                          console.error('Error unlinking appointment:', err);
                        }
                      }}
                      className="flex-shrink-0 text-red-600 hover:text-red-800 p-0.5 rounded"
                      title="Unlink appointment"
                      aria-label="Unlink appointment"
                    >
                      ✕
                    </button>
                  </span>
                )}
                </div>
                {renderReadByMenu()}
              </div>
            </div>
          )}

          <IcsSuggestionBanner emailId={localEmail.id} partitionAt={localEmail.partitionAt} />

          </>
          )}

          <EntityCustomFieldsSection
            entityType={CUSTOM_FIELD_ENTITY.EMAIL}
            entityId={localEmail.id}
            className="px-2 sm:px-3 print:hidden"
            compact
          />

        </div>

        {/* Message body + optional attachments (footer when collapsed, split when expanded) */}
        {nonEmbeddedAttachments.length > 0 ? (
          attachmentsExpanded ? (
            <div ref={bodyAttachmentsRef} className="flex-1 min-h-0 flex flex-col min-w-0">
              <div className="email-detail-body relative z-0 flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
                {renderEmailBodyContent()}
              </div>

              <div
                className={cn(
                  'relative flex-shrink-0 h-1 cursor-row-resize transition-colors touch-none print:hidden',
                  colors.surfaceHover,
                  'hover:bg-indigo-500',
                  isResizingBodyAttachments && colors.primary,
                )}
                onMouseDown={handleAttachmentsSplitMouseDown}
                role="separator"
                aria-orientation="horizontal"
                aria-label="Resize message body and attachments"
              >
                <div className="absolute inset-x-0 -top-2 -bottom-2" aria-hidden />
              </div>

              <div
                className="email-detail-attachments flex-shrink-0 flex flex-col overflow-hidden px-2 sm:px-3 py-2 print:hidden"
                style={{ maxHeight: `${100 - bodySplitPercent}%` }}
              >
                <div className={cn('rounded-lg border px-3 py-2 flex flex-col max-h-full min-h-0 overflow-y-auto', colors.surface, colors.border)}>
                  {renderAttachmentsHeader()}
                  {renderAttachmentItems()}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="email-detail-body relative z-0 flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
                {renderEmailBodyContent()}
              </div>
              <div className={cn('email-detail-attachments flex-shrink-0 border-t px-2 sm:px-3 py-2 print:hidden', colors.border)}>
                <div className={cn('rounded-lg border px-3 py-2', colors.surface, colors.border)}>
                  {renderAttachmentsHeader()}
                </div>
              </div>
            </>
          )
        ) : (
          <div className="email-detail-body relative z-0 flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
            {renderEmailBodyContent()}
          </div>
        )}
      </div>

      <EmailCommentsDrawer
        open={commentsDrawerOpen}
        onClose={() => setCommentsDrawerOpen(false)}
        emailId={localEmail.id}
        partitionAt={localEmail.partitionAt}
        commentCount={localEmail.commentCount ?? 0}
        onCommentAdded={() => {
          const updated = {
            ...localEmail,
            hasComments: true,
            commentCount: (localEmail.commentCount ?? 0) + 1,
          };
          setLocalEmail(updated);
          onEmailUpdate?.(updated);
        }}
      />

      {/* Client Autocomplete Modal */}
      <ClientAutocomplete
        isOpen={showClientAutocomplete}
        onClose={() => setShowClientAutocomplete(false)}
        onSelect={async (clientId) => {
          try {
            await api.post(`/emails/${localEmail.id}/link-client/${clientId}${partitionQuery}`);
            onEmailUpdate?.();
          } catch (error) {
            console.error('Error linking client:', error);
            showError('Failed to link client. Please try again.');
          }
        }}
      />

      {/* Todo Autocomplete Modal */}
      <TodoAutocomplete
        isOpen={showTodoAutocomplete}
        onClose={() => setShowTodoAutocomplete(false)}
        onSelect={async (todoId) => {
          try {
            await api.post(`/emails/${localEmail.id}/link-todo/${todoId}${partitionQuery}`);
            onEmailUpdate?.();
          } catch (error) {
            console.error('Error linking todo:', error);
            showError('Failed to link todo. Please try again.');
          }
        }}
      />

      {/* Appointment Autocomplete Modal */}
      <AppointmentAutocomplete
        isOpen={showAppointmentAutocomplete}
        onClose={() => setShowAppointmentAutocomplete(false)}
        onSelect={async (appointmentId) => {
          try {
            await api.post(`/emails/${localEmail.id}/link-appointment/${appointmentId}${partitionQuery}`);
            onEmailUpdate?.();
          } catch (error) {
            console.error('Error linking appointment:', error);
            showError('Failed to link appointment. Please try again.');
          }
        }}
      />

      <Modal
        isOpen={messageHeadersModalOpen}
        onClose={() => setMessageHeadersModalOpen(false)}
        size="lg"
      >
        <ModalHeader>
          <ModalTitle>Message headers</ModalTitle>
          <button
            type="button"
            onClick={() => setMessageHeadersModalOpen(false)}
            className={cn('p-1.5 rounded-md', colors.textMuted, colors.interactiveHover)}
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </ModalHeader>
        <ModalContent>
          <pre className={cn('text-xs whitespace-pre-wrap break-all font-mono', colors.text)}>
            {localEmail.messageHeaders}
          </pre>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={!!reimportReport}
        onClose={() => setReimportReport(null)}
        size="lg"
      >
        <ModalHeader>
          <ModalTitle>Reimport report</ModalTitle>
          <button
            type="button"
            onClick={() => setReimportReport(null)}
            className={cn('p-1.5 rounded-md', colors.textMuted, colors.interactiveHover)}
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </ModalHeader>
        <ModalContent>
          {reimportReport && (
            <div className={cn('space-y-4 text-sm', colors.text)}>
              <div className={cn('rounded-lg border px-3 py-2', colors.border, 'bg-emerald-50 dark:bg-emerald-950/30')}>
                <p className="font-medium text-emerald-800 dark:text-emerald-200">Message found and updated</p>
                <p className={cn('mt-1 text-xs break-all', colors.textMuted)}>
                  Matched by {reimportReport.matchedBy === 'emailId' ? 'selected email + Message-ID' : 'Message-ID'}
                  {' · '}id {reimportReport.id}
                  {' · '}{reimportReport.folder}
                </p>
                <p className={cn('mt-0.5 text-xs break-all font-mono', colors.textMuted)}>
                  {reimportReport.messageId}
                </p>
              </div>

              <div>
                <h3 className="font-medium mb-1">What changed</h3>
                {reimportReport.changes.length === 0 ? (
                  <p className={colors.textMuted}>No differences listed.</p>
                ) : (
                  <ul className="list-disc pl-5 space-y-1">
                    {reimportReport.changes.map((line) => (
                      <li key={line} className="break-words">{line}</li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <h3 className="font-medium mb-1">Attachments</h3>
                <p className={colors.textMuted}>
                  {reimportReport.attachments.beforeCount} → {reimportReport.attachments.afterCount}
                  {reimportReport.attachments.after.length > 0
                    ? `: ${reimportReport.attachments.after.map((a) => a.filename).join(', ')}`
                    : ' (none)'}
                </p>
              </div>

              <div>
                <h3 className="font-medium mb-1">Left unchanged</h3>
                <p className={colors.textMuted}>{reimportReport.preserved.join(' · ')}</p>
              </div>
            </div>
          )}
        </ModalContent>
      </Modal>

      {/* Print form: header + body + Print button */}
      <EmailPrintForm
        isOpen={printFormOpen}
        email={localEmail}
        onClose={() => setPrintFormOpen(false)}
      />

      <SaveAttachmentsToLibraryModal
        isOpen={saveToLibraryModalOpen}
        onClose={() => setSaveToLibraryModalOpen(false)}
        canSaveCompany={canSaveCompanyToLibrary}
        personalFolders={templatePersonalLibrary.folders}
        personalTemplates={templatePersonalLibrary.templates}
        companyFolders={templateCompanyLibrary.folders}
        personalDocuments={templatePersonalLibrary.documents}
        companyDocuments={templateCompanyLibrary.documents}
        emailId={localEmail.id}
        partitionQuery={partitionQuery}
        attachments={nonEmbeddedAttachments}
        onSaved={({ saved, skipped }) => {
          setSaveToLibraryModalOpen(false);
          void mutateTemplateLibrary();
          if (saved === 0) {
            showError('No supported attachments were saved. Library accepts PDF, Word, and Excel only.');
            return;
          }
          const skippedNote =
            skipped > 0 ? ` ${skipped} unsupported file${skipped !== 1 ? 's' : ''} skipped.` : '';
          showSuccess(
            `Saved ${saved} attachment${saved !== 1 ? 's' : ''} to library.${skippedNote}`,
          );
        }}
      />
    </div>
  );
};

export default EmailDetailView;
